import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import noEnabledAppsSrc from '../../../assets/images/noEnabledApps.svg';
import {DropdownEmpty} from '../../../components/common/dropdowns/dropdownEmpty';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {IntegrationTypeModel} from '../../../models/api/integrationTypesModel';
import {buildSettingsAppsTabUrl} from '../../../models/url/settingsUrlHelpers';
import {SettingsAppsEnabledUrl, SettingsAppsTabsEnum} from '../../../models/url/settingsUrlModel';
import {AppsGrid} from '../layout/appsGrid';
import {SettingsAppCardContainer} from '../settingsAppCardContainer';
import {renderMultiLoadingState} from './settingsAppsPageHelpers';

/*
 * Props.
 */

interface SettingsAppsEnabledPageProps {
  integrationTypes: ReadonlyArray<IntegrationTypeModel>;
  isIntegrationTypesLoading: boolean;
  url: SettingsAppsEnabledUrl;
}

/*
 * Style.
 */

const StyledEmptyStateWrapperDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;
`;

const StyledDropdownEmpty = styled(DropdownEmpty)`
  flex: 0 0 125px;
`;

/*
 * Component.
 */

export const SettingsAppsEnabledPage: FC<SettingsAppsEnabledPageProps> = ({
  integrationTypes,
  isIntegrationTypesLoading,
  url,
}) => {
  const {navigateToLink} = useLinkNavigation();
  const shouldRenderEmptyState = integrationTypes.length === 0 && !isIntegrationTypesLoading;

  return (
    <AppsGrid
      isLoading={isIntegrationTypesLoading}
      shouldHaveLoadingGradient
      renderLoadingState={renderMultiLoadingState}
    >
      {integrationTypes.map((integrationType) => (
        <SettingsAppCardContainer
          key={integrationType.id}
          integrationType={integrationType}
          url={url}
          shouldHideUnavailableIntegration
        />
      ))}
      {shouldRenderEmptyState &&
        renderEmptyState(() => navigateToLink(buildSettingsAppsTabUrl(SettingsAppsTabsEnum.COLLECTIONS)))}
    </AppsGrid>
  );
};

/*
 * Helpers.
 */

function renderEmptyState(onButtonClick: Handler) {
  return (
    <StyledEmptyStateWrapperDiv>
      <StyledDropdownEmpty imageSource={noEnabledAppsSrc}>
        <Trans id="settings-apps-enabled-tab-no-enabled-apps">No enabled apps</Trans>
      </StyledDropdownEmpty>
      <StyledButtonWrapperDiv>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onButtonClick}>
          <Trans id="settings-apps-enabled-tab-no-enabled-apps-button">Explore apps</Trans>
        </Button>
      </StyledButtonWrapperDiv>
    </StyledEmptyStateWrapperDiv>
  );
}
